import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, ArrowDown } from "lucide-react";
import { FaInstagram, FaFacebook, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_faith-bubbles-io/artifacts/m9gzezrc_IMG_9818.JPEG";

const socials = [
  { icon: FaInstagram, href: "https://www.instagram.com/winnerschapel_ipswich/", label: "Instagram", color: "hover:text-pink-500" },
  { icon: FaFacebook, href: "https://www.facebook.com/", label: "Facebook", color: "hover:text-blue-600" },
  { icon: FaXTwitter, href: "https://x.com/", label: "X (Twitter)", color: "hover:text-stone-900" },
  { icon: FaYoutube, href: "https://www.youtube.com/", label: "YouTube", color: "hover:text-red-500" },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

export const Hero = () => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, label: "" });

  useEffect(() => {
    const getNextService = () => {
      const now = new Date();
      const day = now.getDay();
      const h = now.getHours();
      const m = now.getMinutes();

      // Sunday = 0, Wednesday = 3
      // Sunday Service 10:00, Wednesday Service 18:30
      let targetDay, targetHour, targetMin, label;

      const sundayPassed = day > 0 || (day === 0 && (h > 10 || (h === 10 && m >= 0 && h > 10)));
      const isSundayBefore = day === 0 && h < 10;
      const wednesdayPassed = day > 3 || (day === 3 && (h > 18 || (h === 18 && m >= 30)));
      const isWednesdayBefore = day === 3 && (h < 18 || (h === 18 && m < 30));

      if (isSundayBefore) {
        targetDay = 0; targetHour = 10; targetMin = 0; label = "Sunday Service";
      } else if (day >= 0 && day < 3 && !isWednesdayBefore) {
        targetDay = 3; targetHour = 18; targetMin = 30; label = "Wednesday Service";
      } else if (isWednesdayBefore) {
        targetDay = 3; targetHour = 18; targetMin = 30; label = "Wednesday Service";
      } else {
        targetDay = 7; targetHour = 10; targetMin = 0; label = "Sunday Service";
      }

      const target = new Date(now);
      let daysUntil = targetDay - day;
      if (daysUntil < 0) daysUntil += 7;
      if (daysUntil === 0 && (h > targetHour || (h === targetHour && m >= targetMin))) daysUntil = 7;
      target.setDate(now.getDate() + daysUntil);
      target.setHours(targetHour, targetMin, 0, 0);

      const diff = target - now;
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        label,
      };
    };

    setCountdown(getNextService());
    const interval = setInterval(() => setCountdown(getNextService()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-red-50 via-white to-amber-50"
    >
      {/* Decorative bubbles */}
      <div className="bubble-decoration bg-red-500 w-72 h-72 top-20 -left-36 animate-pulse-soft" />
      <div className="bubble-decoration bg-amber-400 w-96 h-96 -bottom-48 -right-48 animate-pulse-soft" style={{ animationDelay: "1s" }} />
      <div className="bubble-decoration bg-red-300 w-40 h-40 top-1/3 right-1/4 animate-pulse-soft" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-32 grid lg:grid-cols-2 gap-12 items-center w-full">
        {/* Left content */}
        <div className="space-y-8">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <span className="font-accent text-5xl sm:text-6xl lg:text-7xl text-red-600 block leading-tight">You are welcome</span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-stone-900 leading-[1.05]"
          >
            Winners Chapel
            <br />
            <span className="text-red-600">International</span>
            <br />
            Ipswich
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base md:text-lg text-stone-600 leading-relaxed max-w-lg"
          >
            Liberating the world through the preaching of the Word of Faith.
            Join us for Spirit-filled worship and life-transforming messages.
          </motion.p>

          {/* Service times pills */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-3"
          >
            <div className="flex items-center gap-2 bg-white rounded-full px-5 py-3 shadow-[0_4px_20px_rgb(0,0,0,0.06)] border border-stone-100" data-testid="service-time-sunday">
              <Clock size={16} className="text-red-600" />
              <span className="text-sm font-semibold text-stone-800">Sunday Service 10:00 AM</span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-full px-5 py-3 shadow-[0_4px_20px_rgb(0,0,0,0.06)] border border-stone-100" data-testid="service-time-wednesday">
              <Clock size={16} className="text-amber-600" />
              <span className="text-sm font-semibold text-stone-800">Wednesday Service 6:30 PM</span>
            </div>
          </motion.div>

          {/* Countdown */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.32 }}
            className="bg-white rounded-2xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.06)] border border-stone-100 max-w-sm"
            data-testid="service-countdown"
          >
            <p className="text-xs font-semibold text-red-600 mb-2">{countdown.label} starts in</p>
            <div className="flex gap-3">
              {[
                { val: countdown.days, unit: "Days" },
                { val: countdown.hours, unit: "Hrs" },
                { val: countdown.minutes, unit: "Min" },
                { val: countdown.seconds, unit: "Sec" },
              ].map((t) => (
                <div key={t.unit} className="text-center">
                  <div className="bg-red-50 rounded-xl w-12 h-12 flex items-center justify-center">
                    <span className="text-lg font-bold text-red-700">{String(t.val).padStart(2, "0")}</span>
                  </div>
                  <span className="text-[10px] text-stone-400 font-medium mt-1 block">{t.unit}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Location */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="flex items-start gap-2 text-stone-500"
          >
            <MapPin size={18} className="mt-0.5 text-red-500 shrink-0" />
            <span className="text-sm">Gainsborough Sports Centre, 5 Braziers Road, IP3 0SP</span>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-4"
          >
            <button
              onClick={() => document.querySelector("#prayer")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-prayer-btn"
              className="bg-red-600 text-white hover:bg-red-700 rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-transform duration-200 hover:-translate-y-1"
            >
              Prayer Request
            </button>
            <button
              onClick={() => document.querySelector("#events")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-events-btn"
              className="bg-white text-red-600 border-2 border-red-200 hover:border-red-400 rounded-full px-8 py-4 font-semibold transition-transform duration-200 hover:-translate-y-1"
            >
              Upcoming Events
            </button>
          </motion.div>

          {/* Social icons */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex gap-3 pt-2"
          >
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={`p-3 rounded-full bg-white shadow-md border border-stone-100 text-stone-500 ${s.color} transition-all duration-200 hover:-translate-y-1 hover:shadow-lg`}
                aria-label={s.label}
              >
                <s.icon size={20} />
              </a>
            ))}
          </motion.div>
        </div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 25, delay: 0.3 }}
          className="hidden lg:block relative"
        >
          <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.12)]">
            <img
              src={HERO_IMG}
              alt="Joyful congregation worshipping"
              className="w-full h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-red-900/20 to-transparent" />
          </div>
          {/* Floating badge */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-stone-100"
          >
            <p className="text-xs text-stone-500 font-medium">Next Service</p>
            <p className="text-lg font-bold text-stone-900">Sunday 10:00 AM</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <button
          onClick={() => document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" })}
          className="p-3 rounded-full bg-white/80 shadow-md hover:bg-white transition-colors"
          aria-label="Scroll down"
          data-testid="scroll-down-btn"
        >
          <ArrowDown size={20} className="text-red-600" />
        </button>
      </motion.div>
    </section>
  );
};
